// KebabMenu — three-dot (⋮) icon button that opens a popover list of
// secondary actions. The standard pattern for collapsing a row of
// "do X / do Y / delete" buttons into one quiet trigger so the
// surrounding UI doesn't shout.
//
// Usage:
//   <KebabMenu
//     ariaLabel="Asset actions"
//     items={[
//       { label: "Edit", onClick: () => setEditing(true) },
//       { label: "Show instructions", onClick: () => toggle() },
//       {
//         label: "Change domain (one-time)",
//         onClick: startChangeDomain,
//         variant: "danger",
//         disabled: domainChangeUsed,
//       },
//     ]}
//   />
//
// Mounted inline; positions the menu absolutely below-right of the
// trigger so it stays anchored to the row even when the row is at
// the right edge of the card. Matches the visual idiom used by
// Gmail / Notion / Linear / GitHub for row-level secondary actions.

import { useEffect, useRef, useState } from "react";

export interface KebabMenuItem {
  label: string;
  onClick: () => void;
  /** "danger" colours the row red, signaling a destructive action. */
  variant?: "default" | "danger";
  disabled?: boolean;
}

interface KebabMenuProps {
  items: KebabMenuItem[];
  /** Required accessible label for the trigger button. */
  ariaLabel: string;
  /** Tailwind classes appended to the trigger button. */
  className?: string;
}

export function KebabMenu({ items, ariaLabel, className = "" }: KebabMenuProps) {
  const [open, setOpen] = useState(false);
  const [keyboardIdx, setKeyboardIdx] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Click outside closes.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Keyboard nav: Esc closes, ArrowUp/Down cycles, Enter activates the
  // currently-focused item. Disabled items are skipped during cycling
  // so the keyboard cursor never lands on something unactionable.
  useEffect(() => {
    if (!open) return;
    function step(direction: 1 | -1) {
      setKeyboardIdx((i) => {
        let next = i;
        for (let n = 0; n < items.length; n++) {
          next = (next + direction + items.length) % items.length;
          if (!items[next]?.disabled) return next;
        }
        return i;
      });
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        step(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        step(-1);
      } else if (e.key === "Enter" && keyboardIdx >= 0) {
        e.preventDefault();
        const it = items[keyboardIdx];
        if (it && !it.disabled) {
          it.onClick();
          setOpen(false);
          triggerRef.current?.focus();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, keyboardIdx, items]);

  // On open, seed keyboard cursor to the first non-disabled item.
  useEffect(() => {
    if (open) {
      const first = items.findIndex((it) => !it.disabled);
      setKeyboardIdx(first >= 0 ? first : 0);
    }
  }, [open, items]);

  // Move DOM focus to the keyboard-active item.
  useEffect(() => {
    if (open && keyboardIdx >= 0) {
      itemRefs.current[keyboardIdx]?.focus();
    }
  }, [open, keyboardIdx]);

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          // Stop click bubbling so the kebab doesn't also trigger the
          // surrounding row's onClick (e.g. opening a detail drawer).
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className={
          "w-9 h-9 flex items-center justify-center " +
          "bg-transparent border border-transparent rounded " +
          "text-muted hover:text-content hover:border-stroke " +
          "transition-colors cursor-pointer " +
          (open ? "text-content border-stroke " : "") +
          className
        }
      >
        {/* Vertical ellipsis. Slightly larger than text to read as an
            icon at any zoom level. */}
        <span className="text-[18px] leading-none" aria-hidden="true">
          ⋮
        </span>
      </button>

      {open && (
        <ul
          role="menu"
          aria-label={ariaLabel}
          // Right-anchored so the menu doesn't overflow the viewport
          // when the kebab sits at the far right of a row. min-w keeps
          // single-word items legible; max-w prevents very long labels
          // from creating a wide popover.
          className="absolute right-0 z-50 mt-1 min-w-[180px] max-w-[260px] rounded animate-fade-in py-1"
          style={{
            background: "rgb(var(--color-bg2))",
            border: "1px solid rgb(var(--color-stroke))",
            boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
          }}
          // Stop click bubbling on the menu itself for the same reason
          // as the trigger.
          onClick={(e) => e.stopPropagation()}
        >
          {items.map((it, i) => {
            const danger = it.variant === "danger";
            return (
              <li key={i} role="none">
                <button
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  type="button"
                  role="menuitem"
                  disabled={it.disabled}
                  onClick={() => {
                    if (it.disabled) return;
                    it.onClick();
                    setOpen(false);
                    triggerRef.current?.focus();
                  }}
                  className={
                    "w-full text-left px-3 py-2 min-h-[36px] " +
                    "font-mono text-[12px] transition-colors " +
                    "border-l-[3px] border-transparent " +
                    (it.disabled
                      ? "text-dim cursor-not-allowed "
                      : danger
                        ? "text-danger hover:bg-danger-bg focus:bg-danger-bg focus:outline-none cursor-pointer "
                        : "text-content hover:bg-bg2 focus:bg-bg2 focus:outline-none cursor-pointer ")
                  }
                >
                  {it.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
